import React from "react";
import { Tooltip } from "@mui/material";
import { PortfolioLiquidityRiskItem } from "@/types/client";
import { formatCurrencyInCrores } from "@/utils/formatters";
import { fmtNum } from "./utils";

/**
 * Risk rule: a target position (Analyst / IC / CIO target weight, as a % of the
 * portfolio) must not exceed the company's liquidity `max_permissible_position`
 * from the Portfolio Liquidity Risk API — also a % of the portfolio, so the two
 * are directly comparable.
 *
 * The check is against the Core Domestic portfolio only.
 *
 * This name must match `InvestmentStrategy.name` (Scheme.investment_strategy_display).
 */
export const CORE_DOMESTIC_STRATEGY = "Core Domestic";

/** The Core liquidity limit for one company (present only when median data exists). */
export interface CompanyRisk {
  core?: PortfolioLiquidityRiskItem;
}

/** Keyed by company id, not symbol: a newly listed company can have no symbol yet. */
export type RiskByCompanyId = Record<number, CompanyRisk>;

export interface RiskLookup {
  /** Core limit per company id (only companies with median data available). */
  riskByCompanyId: RiskByCompanyId;
  /** Company ids that appear in the risk data but have no median value. */
  missingCompanyIds: number[];
}

/**
 * Build a per-company lookup of Core-Domestic risk limits. Companies missing median
 * data are omitted from `riskByCompanyId` and collected separately so the UI can disclaim them.
 */
export const buildRiskLookup = (coreItems: PortfolioLiquidityRiskItem[]): RiskLookup => {
  const riskByCompanyId: RiskByCompanyId = {};
  const missingCompanyIds = new Set<number>();

  coreItems.forEach((item) => {
    if (!item.is_median_value_available) {
      missingCompanyIds.add(item.company_id);
      return;
    }
    (riskByCompanyId[item.company_id] ??= {}).core = item;
  });

  return { riskByCompanyId, missingCompanyIds: Array.from(missingCompanyIds) };
};

const toNumber = (targetPct: number | string | null | undefined): number | null => {
  const n = typeof targetPct === "string" ? parseFloat(targetPct.replace(/,/g, "")) : targetPct;
  return n == null || isNaN(n) ? null : n;
};

const breaches = (
  target: number | null,
  item: PortfolioLiquidityRiskItem | undefined | null,
): boolean => item != null && target != null && target > item.max_permissible_position;

/**
 * Whether a target weight breaches the Core portfolio's limit. Used for the CIO target,
 * which is per-row — only CORE rows carry a Core-comparable target, so LHF rows are
 * never checked (callers pass `undefined`/`null` for those).
 */
export const isTargetOverLimit = (
  targetPct: number | string | null | undefined,
  item: PortfolioLiquidityRiskItem | undefined | null,
): boolean => breaches(toNumber(targetPct), item);

/** Whether a target weight breaches the Core portfolio's limit. */
export const getTargetRiskStatus = (
  targetPct: number | string | null | undefined,
  risk: CompanyRisk | null | undefined,
): { hasBreach: boolean } => ({ hasBreach: breaches(toNumber(targetPct), risk?.core) });

/** One tooltip line per portfolio: its max-permissible %, whether the target breached it, and liquidity detail. */
const portfolioLine = (
  name: string,
  item: PortfolioLiquidityRiskItem | undefined,
  target: number | null,
): React.ReactNode => {
  if (item == null) {
    return (
      <span style={{ opacity: 0.7 }}>
        {name}: no liquidity data
        <br />
      </span>
    );
  }
  const breached = breaches(target, item);
  return (
    <span style={{ color: breached ? "#ffb4ab" : "#a5d6a7", fontWeight: 600 }}>
      {name} max: {fmtNum(item.max_permissible_position, 1, 0)}%{breached ? " — breached" : " — ok"}
      <br />
      <span style={{ color: "inherit", opacity: 0.85, fontWeight: 400 }}>
        &nbsp;&nbsp;Current Size ₹{formatCurrencyInCrores(item.notional, "INR", true)} Cr · Median Traded Value ₹{formatCurrencyInCrores(item.median_tv_21d, "INR", true)} Cr
      </span>
      <br />
    </span>
  );
};

/**
 * The ⚠ marker shown next to a target that breaches the Core liquidity limit. Hover reveals
 * the max-permissible position, whether the target violates it, along with the company's
 * current size and 21-day median traded volume.
 */
export const RiskWarningIcon: React.FC<{
  label: string;
  targetPct: number | string;
  risk: CompanyRisk;
}> = ({ label, targetPct, risk }) => {
  // Callers only render this icon after confirming a breach, which requires risk.core
  // to be non-null — guard explicitly so a loosened call-site fails closed, not with a
  // confusing "no liquidity data" line under a "breach" title.
  if (!risk.core) return null;
  const target = toNumber(targetPct);
  return (
    <Tooltip
      placement="top"
      title={
        <span>
          <span style={{ fontWeight: 600, color: "#ffb4ab" }}>⚠ Liquidity risk breach</span>
          <br />
          {label} target {fmtNum(target)}%
          <br />
          {portfolioLine("Core", risk.core, target)}
        </span>
      }
    >
      <span style={{ color: "var(--neg)", fontWeight: 700, marginLeft: 3, cursor: "help" }}>⚠</span>
    </Tooltip>
  );
};
